import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';

export default function PortalAccessReview() {
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState(true);
  const load = useCallback(() => api.get('/api/portal-access/review', { params: { pending } }).then(({ data }) => setRows(data)).catch(() => toast.error('Could not load review list')), [pending]);
  useEffect(() => { load(); }, [load]);

  const act = (p, msg) => p.then(() => { toast.success(msg); load(); }).catch((e) => toast.error(e.response?.data?.detail || 'Action failed'));

  return (
    <div className="p-6 lg:p-10 space-y-8" data-testid="portal-access-review-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-headings font-bold flex items-center gap-3"><ShieldCheck className="w-8 h-8" />Portal access review</h1>
          <p className="text-sm text-muted-foreground mt-2">Grants created by the migration from group-wide access are pending until a staff member reviews them. Revoke anything a user should not see.</p>
        </div>
        <label className="flex items-center gap-2 text-sm"><Switch checked={pending} onCheckedChange={setPending} data-testid="portal-review-pending-toggle" />Pending only</label>
      </div>
      <div className="rounded-xl border border-border divide-y divide-border bg-card">
        {rows.length === 0 && <p className="p-6 text-sm text-muted-foreground" data-testid="portal-review-empty">Nothing to review.</p>}
        {rows.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-4 p-4" data-testid={`portal-review-row-${r.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium">{r.user_name || r.user_email} <span className="text-muted-foreground font-normal">→</span> <Link className="underline" to={`/clients/${r.client_id}`}>{r.client_name}</Link></p>
              <p className="text-xs text-muted-foreground">{r.user_email} · {r.note || r.source}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant={r.reviewed_at ? 'outline' : 'secondary'}>{r.reviewed_at ? 'Reviewed' : 'Pending review'}</Badge>
              {!r.reviewed_at && <Button size="sm" variant="outline" onClick={() => act(api.post(`/api/portal-access/grants/${r.id}/reviewed`), 'Kept – marked reviewed')} data-testid={`portal-review-keep-${r.id}`}>Keep</Button>}
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => act(api.post(`/api/portal-access/grants/${r.id}/revoke`, { reason: 'Access review' }), 'Access revoked')} data-testid={`portal-review-revoke-${r.id}`}>Revoke</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
